import { writable, type Readable } from 'svelte/store';
import type {
  AdminGuestlistResponse,
  AdminInventorySkuRecord,
  AdminOrderRecord,
  AdminPersonRecord,
  CatalogEventSummary,
  CommunityDetail,
  CommunityMemberEntry,
  CommunityQuestionnaire,
  CommunitySummary,
  EventApplicationEntry,
  EventConfig,
  EventGuestEntry,
  EventSummary,
  InventoryItem,
  InviteRecord,
  MyMembership,
  StandingEventConfig,
  StandingEventDetail,
  UserTicketSummary
} from './types';
import type { AdminEventConfig } from './convexApi';
import { getConvex } from './convexClient';
import { getFunctionName } from 'convex/server';
import { api } from '../convex/_generated/api';
import {
  mapAdminEventConfig,
  mapManagedEventConfig,
  type ManagedEventConfig,
  mapEventConfig,
  mapEventSummary,
  mapTicketExtras,
  mapPersonSummary,
  mapInventoryItem,
  mapInviteRecord,
  mapTicketUser
} from './convexApi';
import type { ValidationResponse } from './types';

export interface LiveQueryState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

export interface LiveQueryHandle<T, Args> extends Readable<LiveQueryState<T>> {
  refresh: () => Promise<T | null>;
  setArgs: (args: Args) => Promise<T | null>;
  dispose: () => void;
}

type QueryDescriptor<Args> = {
  ref: any;
};

type Mapper<Result> = (value: any) => Result;

function normalizeError(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  return 'Request failed';
}

// Stale-while-revalidate seed cache. On back-nav a page's live-query store is
// destroyed and recreated; without a seed it renders {data: null, loading: true}
// (a short LoadingBlock), which prevents the browser from restoring scroll until
// the round-trip lands. Seeding from here lets the re-mounted page render its
// last-known MAPPED data synchronously; the subscription + runQuery still run and
// replace the seed within one round-trip.
//
// Auth safety: cached data is standing/redaction-sensitive (a signed-out visitor
// sees redacted events; a member sees full data). clearLiveQueryCache() is called
// on every auth identity transition (see auth.ts) so no cross-identity data leaks.
const liveQueryCache = new Map<string, unknown>();

/** Key a query by its Convex function name plus serialized args. */
function cacheKey(descriptor: QueryDescriptor<unknown>, args: unknown): string {
  return `${getFunctionName(descriptor.ref)}|${JSON.stringify(args)}`;
}

/** Drop all seeded data. Call whenever the Convex auth identity changes. */
export function clearLiveQueryCache(): void {
  liveQueryCache.clear();
}

function createLiveQueryStore<Result, Args>(descriptor: QueryDescriptor<Args>, initialArgs: Args, mapper?: Mapper<Result>): LiveQueryHandle<Result, Args> {
  const convex = getConvex();
  let currentArgs = initialArgs;
  let key = cacheKey(descriptor, currentArgs);
  const seeded = liveQueryCache.has(key);
  const store = writable<LiveQueryState<Result>>(
    seeded
      ? { data: liveQueryCache.get(key) as Result, loading: false, error: null }
      : { data: null, loading: true, error: null }
  );
  let unsubscribe: (() => void) | null = null;
  let generation = 0;

  const apply = (value: any) => (mapper ? mapper(value) : (value as Result));

  const subscribe = store.subscribe;

  const attachSubscription = (token: number) => {
    unsubscribe?.();
    try {
      unsubscribe = convex.onUpdate(descriptor.ref, currentArgs, (value: any) => {
        if (token !== generation) return;
        try {
          const mapped = apply(value);
          liveQueryCache.set(key, mapped);
          store.set({ data: mapped, loading: false, error: null });
        } catch (err) {
          store.set({ data: null, loading: false, error: normalizeError(err) });
        }
      });
    } catch (error) {
      store.set({ data: null, loading: false, error: normalizeError(error) });
    }
  };

  const runQuery = async (token: number): Promise<Result | null> => {
    store.update(state => ({ ...state, loading: true, error: null }));
    try {
      const result = await convex.query(descriptor.ref, currentArgs);
      if (token !== generation) return null;
      const mapped = apply(result);
      liveQueryCache.set(key, mapped);
      store.set({ data: mapped, loading: false, error: null });
      return mapped;
    } catch (error) {
      if (token === generation) {
        store.update(state => ({ ...state, loading: false, error: normalizeError(error) }));
      }
      return null;
    }
  };

  const restart = async () => {
    const token = ++generation;
    attachSubscription(token);
    return await runQuery(token);
  };

  const refresh = async () => {
    const token = generation;
    return await runQuery(token);
  };

  const setArgs = async (args: Args) => {
    currentArgs = args;
    key = cacheKey(descriptor, currentArgs);
    // Seed the store for the new key if we have it; otherwise reset to loading so
    // the previous args' data is never shown for the wrong args.
    if (liveQueryCache.has(key)) {
      store.set({ data: liveQueryCache.get(key) as Result, loading: false, error: null });
    } else {
      store.set({ data: null, loading: true, error: null });
    }
    return await restart();
  };

  const dispose = () => {
    unsubscribe?.();
    unsubscribe = null;
  };

  // Prime the store immediately.
  void restart();

  return { subscribe, refresh, setArgs, dispose };
}

// --- Admin live stores ------------------------------------------------------

export function liveAdminEventsStore() {
  return createLiveQueryStore<AdminEventConfig[], {}>(
    { ref: api.events.listConfigs },
    {},
    (result: any) => (Array.isArray(result) ? result.map(mapAdminEventConfig) : [])
  );
}

export function liveAdminGuestlistStore(eventId: string) {
  return createLiveQueryStore<AdminGuestlistResponse | null, { eventId: string }>(
    { ref: api.admin.listGuestlist },
    { eventId },
    (value: any) => value as AdminGuestlistResponse
  );
}

export function liveAdminPeopleDirectoryStore() {
  return createLiveQueryStore<AdminPersonRecord[], {}>(
    { ref: api.admin.listPeople },
    {},
    (result: any) => (Array.isArray(result) ? (result as AdminPersonRecord[]) : [])
  );
}

export function liveAdminOrdersStore(args: { limit?: number; eventId?: string } = {}) {
  return createLiveQueryStore<AdminOrderRecord[], { limit?: number; eventId?: string }>(
    { ref: api.admin.listOrders },
    args,
    (result: any) => (Array.isArray(result) ? (result as AdminOrderRecord[]) : [])
  );
}

/** Full event configs for a community's manage area (managers only). */
export function liveManagedEventsStore(communityId: string) {
  return createLiveQueryStore<ManagedEventConfig[], { communityId: string }>(
    { ref: api.events.listForCommunity },
    { communityId },
    (result: any) => (Array.isArray(result) ? result.map(mapManagedEventConfig) : [])
  );
}

export function liveAdminInventoryStore(eventId: string) {
  return createLiveQueryStore<AdminInventorySkuRecord[] | null, { eventId: string }>(
    { ref: api.admin.listInventory },
    { eventId },
    (result: any) => (Array.isArray(result) ? (result as AdminInventorySkuRecord[]) : null)
  );
}

// --- Public site live stores ------------------------------------------------

export function liveEventSummariesStore() {
  return createLiveQueryStore<EventSummary[], {}>(
    { ref: api.events.list },
    {},
    (result: any) => (Array.isArray(result) ? result.map(mapEventSummary) : [])
  );
}

export function liveEventDetailStore(eventId: string) {
  return createLiveQueryStore<{ event: EventConfig; inventory: InventoryItem[]; ticketPrice: number } | null, { eventId: string }>(
    { ref: api.events.detail },
    { eventId },
    (result: any) => {
      if (!result || !result.event) {
        return result;
      }
      return {
        event: mapEventConfig(result.event),
        inventory: (result.inventory ?? []).map(mapInventoryItem),
        ticketPrice: result.ticketPrice ?? 0
      };
    }
  );
}

/**
 * Standing-aware public event detail (events:detail, two-lists slice 01) for
 * the /e/ page. Unlike liveEventDetailStore this preserves `standing`,
 * `redacted`, `tiers`, `eventCapacity` and `soldOut`. When redacted, the raw
 * event doc has null `location`/`ticket`; mapEventConfig can't be used, so the
 * mapper builds a safe EventConfig by hand with the ticket config defaulted.
 */
export function liveStandingEventDetailStore(eventId: string, inviteCode?: string) {
  // inviteCode lets a signed-out `?invite=` landing open an unlisted event's
  // redacted pitch — the server validates the code before unlocking detail.
  return createLiveQueryStore<StandingEventDetail | null, { eventId: string; inviteCode?: string }>(
    { ref: api.events.detail },
    inviteCode ? { eventId, inviteCode } : { eventId },
    (result: any): StandingEventDetail | null => {
      if (!result || !result.event) return result ?? null;
      const doc = result.event;
      const event: StandingEventConfig = {
        id: doc.eventId,
        name: doc.name,
        headline: doc.headline,
        description: doc.description ?? null,
        flyerUrl: doc.flyerUrl ?? null,
        whatsappGroupUrl: doc.whatsappGroupUrl ?? null,
        applicationUrl: doc.applicationUrl ?? null,
        // Redacted for strangers → null. Kept nullable-tolerant here.
        location: doc.location ?? null,
        schedule: doc.schedule,
        ticket: doc.ticket
          ? {
              productName: doc.ticket.productName,
              currency: doc.ticket.currency,
              startPrice: doc.ticket.startPrice,
              endPrice: doc.ticket.endPrice,
              dynamicPricing: doc.ticket.dynamicPricing ?? undefined,
              allowInvites: doc.ticket.allowInvites ?? undefined,
              defaultInviteAllowance: doc.ticket.defaultInviteAllowance ?? undefined,
              preCheckoutQuestion: doc.ticket.preCheckoutQuestion ?? null
            }
          : null,
        extras: (doc.extras ?? []).map((extra: any) => ({
          sku: extra.sku,
          name: extra.name,
          description: extra.description ?? undefined,
          price: extra.price,
          currency: extra.currency,
          capacity: extra.capacity,
          maxPerOrder: extra.maxPerOrder,
          isUnlimited: typeof extra.capacity === 'number' && extra.capacity < 0
        }))
      };
      return {
        event,
        inventory: (result.inventory ?? []).map(mapInventoryItem),
        tiers: result.tiers ?? [],
        ticketPrice: result.ticketPrice ?? null,
        eventCapacity: result.eventCapacity ?? null,
        soldOut: Boolean(result.soldOut),
        standing: result.standing ?? 'stranger',
        redacted: Boolean(result.redacted),
        inviterName: result.inviterName ?? null,
        inviteCode: result.inviteCode ?? null,
        inviteKind: result.inviteKind ?? null
      };
    }
  );
}

export function liveTicketsByPersonStore(personId: string) {
  return createLiveQueryStore<UserTicketSummary[], { personId: string }>(
    { ref: api.tickets.listByPerson },
    { personId },
    (result: any) => {
      if (!Array.isArray(result)) return [];
      return result.map(ticket => {
        const extras = mapTicketExtras(ticket.extras ?? []);
        const person = mapPersonSummary(ticket.person, ticket.personId ? String(ticket.personId) : undefined);
        return {
          id: typeof ticket._id === 'string' ? ticket._id : ticket._id?._id ?? ticket.code,
          code: ticket.code,
          eventId: ticket.eventId,
          person,
          redeemed: ticket.status === 'redeemed',
          redeemedAt: ticket.redeemedAt ?? null,
          extras,
          status: ticket.status,
          origin: ticket.origin ?? undefined
        } satisfies UserTicketSummary;
      });
    }
  );
}

export function liveInvitesByTicketStore(ticketId: string) {
  return createLiveQueryStore<InviteRecord[], { ticketId: string }>(
    { ref: api.invites.listByInviter },
    { ticketId },
    (result: any) => (Array.isArray(result) ? result.map(mapInviteRecord) : [])
  );
}

export function liveInvitesByEventStore(eventId: string) {
  return createLiveQueryStore<InviteRecord[], { eventId: string; status?: string }>(
    { ref: api.invites.listByEvent },
    { eventId },
    (result: any) => (Array.isArray(result) ? result.map(mapInviteRecord) : [])
  );
}

/**
 * Fully-mapped live ticket detail: same payload shape as fetchConvexTicketValidation,
 * but reactive — ticket status, inventory, and pricing update without a refresh.
 */
export function liveTicketDetailStore(eventId: string, code: string) {
  return createLiveQueryStore<ValidationResponse | null, { eventId: string; code: string }>(
    { ref: api.tickets.detail },
    { eventId, code },
    (result: any) => {
      if (!result || !result.ticket || !result.event) return null;
      return {
        user: mapTicketUser(result.ticket),
        event: mapEventConfig(result.event),
        inventory: (result.inventory ?? []).map(mapInventoryItem),
        community: result.community ?? null,
        tiers: result.tiers ?? [],
        eventCapacity: result.eventCapacity ?? null,
        ticketPrice: result.ticketPrice ?? result.event?.ticket?.startPrice ?? 0
      };
    }
  );
}

export function liveGuestTicketDetailStore(eventId: string, code: string) {
  return createLiveQueryStore<ValidationResponseLike | null, { eventId: string; code: string }>(
    { ref: api.tickets.detail },
    { eventId, code },
    (result: any) => result as ValidationResponseLike
  );
}

type ValidationResponseLike = {
  ticket: any;
  event: any;
  inventory: any[];
  ticketPrice: number;
};

// --- Communities --------------------------------------------------------------

export function liveCommunitiesStore() {
  return createLiveQueryStore<CommunitySummary[], {}>(
    { ref: api.communities.list },
    {},
    (result: any) => (Array.isArray(result) ? (result as CommunitySummary[]) : [])
  );
}

export function liveCommunityDetailStore(slug: string) {
  return createLiveQueryStore<CommunityDetail | null, { slug: string }>(
    { ref: api.communities.detail },
    { slug },
    (result: any) => (result ?? null) as CommunityDetail | null
  );
}

export function liveCommunityMembersStore(communityId: string) {
  return createLiveQueryStore<CommunityMemberEntry[], { communityId: string }>(
    { ref: api.communities.listMembers },
    { communityId },
    (result: any) => (Array.isArray(result) ? (result as CommunityMemberEntry[]) : [])
  );
}

export function liveMyMembershipsStore() {
  return createLiveQueryStore<MyMembership[], {}>(
    { ref: api.communities.myMemberships },
    {},
    (result: any) => (Array.isArray(result) ? (result as MyMembership[]) : [])
  );
}

// --- Catalog, questionnaires, event applications, guests ----------------------

export function liveCatalogStore() {
  return createLiveQueryStore<CatalogEventSummary[], {}>(
    { ref: api.events.listCatalog },
    {},
    (result: any) => (Array.isArray(result) ? (result as CatalogEventSummary[]) : [])
  );
}

export function liveQuestionnaireStore(communityId: string) {
  return createLiveQueryStore<CommunityQuestionnaire | null, { communityId: string }>(
    { ref: api.questionnaires.get },
    { communityId },
    (result: any) => (result ?? null) as CommunityQuestionnaire | null
  );
}

export function liveApplicantQuestionnaireStore(slug: string) {
  return createLiveQueryStore<CommunityQuestionnaire | null, { slug: string }>(
    { ref: api.questionnaires.forApplicant },
    { slug },
    (result: any) => (result ?? null) as CommunityQuestionnaire | null
  );
}

export function liveEventApplicationsStore(eventId: string) {
  return createLiveQueryStore<EventApplicationEntry[], { eventId: string }>(
    { ref: api.eventApplications.listByEvent },
    { eventId },
    (result: any) => (Array.isArray(result) ? (result as EventApplicationEntry[]) : [])
  );
}

export function liveMyEventApplicationStore(eventId: string) {
  return createLiveQueryStore<{ status: string; createdAt: number } | null, { eventId: string }>(
    { ref: api.eventApplications.mine },
    { eventId },
    (result: any) => (result ?? null) as { status: string; createdAt: number } | null
  );
}

export function liveEventGuestsStore(eventId: string) {
  return createLiveQueryStore<EventGuestEntry[], { eventId: string }>(
    { ref: api.tickets.listForManager },
    { eventId },
    (result: any) => (Array.isArray(result) ? (result as EventGuestEntry[]) : [])
  );
}

/**
 * Live tail of a shared drawing canvas: only strokes drawn after `after`.
 * The component loads history once (fetchCanvasStrokes) and re-anchors this
 * store via setArgs as the tail grows, so each reactive update stays small.
 */
export function liveCanvasTailStore(canvasKey: string, after: number) {
  return createLiveQueryStore<
    import('./convexApi').CanvasStroke[],
    { canvasKey: string; after: number }
  >({ ref: api.canvasStrokes.list }, { canvasKey, after }, (result: any) =>
    Array.isArray(result) ? result : []
  );
}
